import { create } from 'zustand'
import {
  collection, doc, addDoc, getDoc, getDocs, deleteDoc, updateDoc,
  query, where, serverTimestamp,
} from 'firebase/firestore'
import { ref as dbRef, set as rtdbSet } from 'firebase/database'
import { httpsCallable } from 'firebase/functions'
import { db, rtdb, getFunctionsInstance } from '../firebase'
import { useAuthStore } from './auth'
import { getEffectiveRoles } from './impersonation'
import { USER_ROLES } from '../constants/roles'
import type { Timestamp } from 'firebase/firestore'

type FirestoreTimestamp = Timestamp | null

export interface EnrolledPhotoMeta {
  photoUrl?: string
  showPhotoOnSOS?: boolean
  emoji: string
  nickname?: string
  age?: number
}

export interface OrgInvite {
  id: string
  type: 'dependent' | 'staff'
  orgId: string
  orgName: string
  senderUid: string
  senderName: string
  inviteeEmail: string
  status: 'pending' | 'accepted' | 'declined'
  sentAt: FirestoreTimestamp
}

export interface OrgEnrollment {
  id: string
  orgId: string
  orgName: string
  familyUid: string
  profileId: string
  profileName: string
  permissions: {
    sessionNotes: boolean
    location: boolean
    sosAlerts: boolean
    notifyOrgOnGuardianSOS?: boolean
    orgCanResolveGuardianSOS?: boolean
    /** Field-level passport grants — see @/constants/passportFields. */
    passportFields?: string[]
  }
  enrolledAt: FirestoreTimestamp
  inviteId: string
  assignedStaffUids?: string[]
}

export interface OrgStaffMember {
  uid: string
  orgId: string
  orgName: string
  displayName: string
  email: string
  photoURL: string | null
  joinedAt: FirestoreTimestamp
}

function getOrgContext(): { orgId: string; orgName: string } | null {
  const roles = getEffectiveRoles()
  const orgRole = roles.find(r =>
    r.role === USER_ROLES.ORG_ADMIN || r.role === USER_ROLES.STAFF
  )
  if (!orgRole?.orgId) return null
  return { orgId: orgRole.orgId, orgName: orgRole.orgName ?? 'My Organization' }
}

interface OrgInvitesState {
  sentInvites: OrgInvite[]
  enrollments: OrgEnrollment[]
  staff: OrgStaffMember[]
  loading: boolean
  initialized: boolean
  enrolledPhotoCache: Record<string, EnrolledPhotoMeta>
  familyNames: Record<string, string>
  familyPhotos: Record<string, string | null>

  fetchSentInvites: () => Promise<void>
  fetchEnrollments: () => Promise<void>
  fetchStaff: () => Promise<void>
  sendInvite: (type: 'dependent' | 'staff', inviteeEmail: string) => Promise<void>
  cancelInvite: (inviteId: string) => Promise<void>
  fetchFamilyProfileEnrollments: (profileId: string) => Promise<OrgEnrollment[]>
  revokeFamilyEnrollment: (enrollmentId: string, orgId: string) => Promise<void>
  updateEnrollmentPermissions: (enrollmentId: string, permissions: OrgEnrollment['permissions']) => Promise<void>
  fetchFamilyNames: () => Promise<void>
  fetchEnrolledPhotos: () => Promise<void>
  initOrgData: () => Promise<void>
  updateAssignedStaff: (enrollmentId: string, staffUids: string[]) => Promise<void>
  assignedEnrollments: () => OrgEnrollment[]
  familyLabel: (uid: string) => string
  acceptDependentInvite: (
    invite: OrgInvite,
    selections: Array<{
      profileId: string
      profileName: string
      familyUid: string
      permissions: {
        sessionNotes: boolean
        location: boolean
        sosAlerts: boolean
        notifyOrgOnGuardianSOS?: boolean
        orgCanResolveGuardianSOS?: boolean
        passportFields?: string[]
      }
    }>,
  ) => Promise<void>
  acceptStaffInvite: (invite: OrgInvite) => Promise<void>
  declineOrgInvite: (inviteId: string) => Promise<void>
}

export const useOrgInvitesStore = create<OrgInvitesState>()((set, get) => ({
  sentInvites: [],
  enrollments: [],
  staff: [],
  loading: false,
  initialized: false,
  enrolledPhotoCache: {},
  familyNames: {},
  familyPhotos: {},

  async fetchSentInvites() {
    const ctx = getOrgContext()
    if (!ctx) return
    set({ loading: true })
    try {
      const q = query(collection(db, 'org_invites'), where('orgId', '==', ctx.orgId), where('status', '==', 'pending'))
      const snap = await getDocs(q)
      set({ sentInvites: snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<OrgInvite, 'id'>) })) })
    } catch (err) {
      console.warn('[orgInvites] fetchSentInvites failed:', err)
    } finally {
      set({ loading: false })
    }
  },

  async fetchEnrollments() {
    const ctx = getOrgContext()
    if (!ctx) return
    set({ loading: true })
    try {
      const q = query(collection(db, 'org_enrollments'), where('orgId', '==', ctx.orgId))
      const snap = await getDocs(q)
      set({ enrollments: snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<OrgEnrollment, 'id'>) })) })
    } catch (err) {
      console.warn('[orgInvites] fetchEnrollments failed:', err)
    } finally {
      set({ loading: false })
    }
  },

  async fetchStaff() {
    const ctx = getOrgContext()
    if (!ctx) return
    set({ loading: true })
    try {
      const q = query(collection(db, 'org_staff'), where('orgId', '==', ctx.orgId))
      const snap = await getDocs(q)
      set({ staff: snap.docs.map(d => d.data() as OrgStaffMember) })
    } catch (err) {
      console.warn('[orgInvites] fetchStaff failed:', err)
    } finally {
      set({ loading: false })
    }
  },

  async sendInvite(type, inviteeEmail) {
    const ctx = getOrgContext()
    if (!ctx) throw new Error('No org context')
    const auth = useAuthStore.getState()
    const uid = auth.uid()
    if (!uid) throw new Error('Not authenticated')
    const data = {
      type,
      orgId: ctx.orgId,
      orgName: ctx.orgName,
      senderUid: uid,
      senderName: auth.user?.displayName ?? '',
      inviteeEmail,
      status: 'pending' as const,
      sentAt: serverTimestamp(),
    }
    const docRef = await addDoc(collection(db, 'org_invites'), data)
    set(s => ({
      sentInvites: [...s.sentInvites, { id: docRef.id, ...data, sentAt: null }],
    }))
  },

  async cancelInvite(inviteId) {
    await deleteDoc(doc(db, 'org_invites', inviteId))
    set(s => ({ sentInvites: s.sentInvites.filter(i => i.id !== inviteId) }))
  },

  async fetchFamilyProfileEnrollments(profileId) {
    const uid = useAuthStore.getState().uid()
    if (!uid) return []
    try {
      const q = query(
        collection(db, 'org_enrollments'),
        where('familyUid', '==', uid),
        where('profileId', '==', profileId),
      )
      const snap = await getDocs(q)
      return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<OrgEnrollment, 'id'>) }))
    } catch (err) {
      console.warn('[orgInvites] fetchFamilyProfileEnrollments failed:', err)
      return []
    }
  },

  async revokeFamilyEnrollment(enrollmentId, _orgId) {
    try {
      await deleteDoc(doc(db, 'org_enrollments', enrollmentId))
    } catch (err) {
      console.warn('[orgInvites] revokeFamilyEnrollment failed:', err)
      throw err
    }
  },

  async updateEnrollmentPermissions(enrollmentId, permissions) {
    try {
      await updateDoc(doc(db, 'org_enrollments', enrollmentId), { permissions })
    } catch (err) {
      console.warn('[orgInvites] updateEnrollmentPermissions failed:', err)
      throw err
    }
  },

  async acceptDependentInvite(invite, selections) {
    for (const sel of selections) {
      const existing = await getDocs(
        query(
          collection(db, 'org_enrollments'),
          where('orgId', '==', invite.orgId),
          where('familyUid', '==', sel.familyUid),
          where('profileId', '==', sel.profileId),
        ),
      )
      if (!existing.empty) {
        console.warn(`[acceptDependentInvite] enrollment already exists for ${sel.profileId}, skipping`)
        continue
      }
      try {
        await addDoc(collection(db, 'org_enrollments'), {
          orgId: invite.orgId,
          orgName: invite.orgName,
          familyUid: sel.familyUid,
          profileId: sel.profileId,
          profileName: sel.profileName,
          permissions: sel.permissions,
          enrolledAt: serverTimestamp(),
          inviteId: invite.id,
        })
      } catch (err) {
        console.warn('[orgInvites] acceptDependentInvite — enrollment create failed:', err)
        throw err
      }
      try {
        await rtdbSet(dbRef(rtdb, `family_orgs/${sel.familyUid}`), invite.orgId)
      } catch (err) {
        console.warn('[orgInvites] acceptDependentInvite — RTDB mirror failed:', err)
        throw err
      }
    }
    try {
      await updateDoc(doc(db, 'org_invites', invite.id), { status: 'accepted' })
    } catch (err) {
      console.warn('[orgInvites] acceptDependentInvite — status update failed:', err)
      throw err
    }
  },

  async acceptStaffInvite(invite) {
    const fn = httpsCallable(await getFunctionsInstance(), 'acceptStaffInvite')
    await fn({ inviteId: invite.id })
  },

  async declineOrgInvite(inviteId) {
    try {
      await updateDoc(doc(db, 'org_invites', inviteId), { status: 'declined' })
    } catch (err) {
      console.warn('[orgInvites] declineOrgInvite failed:', err)
      throw err
    }
  },

  async fetchFamilyNames() {
    const enrollments = get().enrollments
    const uids = [...new Set(enrollments.map(e => e.familyUid))]
    const current = get().familyNames
    await Promise.all(uids.map(async uid => {
      if (current[uid]) return
      try {
        const snap = await getDoc(doc(db, 'users', uid))
        if (snap.exists()) {
          const data = snap.data() as { displayName?: string; email?: string; photoURL?: string }
          const name = data.displayName || data.email?.split('@')[0] || ''
          if (name) set(s => ({ familyNames: { ...s.familyNames, [uid]: name } }))
          set(s => ({ familyPhotos: { ...s.familyPhotos, [uid]: data.photoURL ?? null } }))
        }
      } catch (err) {
        console.warn('[orgInvites.fetchFamilyNames] failed:', err)
      }
    }))
  },

  async fetchEnrolledPhotos() {
    const enrollments = get().enrollments
    await Promise.all(enrollments.map(async e => {
      const key = `${e.familyUid}_${e.profileId}`
      if (get().enrolledPhotoCache[key]) return
      try {
        const snap = await getDoc(doc(db, 'users', e.familyUid, 'profiles', e.profileId))
        if (snap.exists()) {
          const d = snap.data() as Record<string, unknown>
          const photoUrl = typeof d.photoUrl === 'string' ? d.photoUrl : undefined
          if (photoUrl && d.showPhotoOnSOS === true) {
            const img = new Image(); img.src = photoUrl
          }
          set(s => ({
            enrolledPhotoCache: {
              ...s.enrolledPhotoCache,
              [key]: {
                photoUrl,
                showPhotoOnSOS: d.showPhotoOnSOS === true,
                emoji: typeof d.emoji === 'string' ? d.emoji : '🧒',
                nickname: typeof d.nickname === 'string' ? d.nickname : undefined,
                age: typeof d.age === 'number' ? d.age : undefined,
              },
            },
          }))
        }
      } catch (err) {
        console.warn('[orgInvites.fetchEnrolledPhotos] failed:', err)
      }
    }))
  },

  async initOrgData() {
    if (get().initialized) return
    await get().fetchEnrollments()
    await get().fetchFamilyNames()
    await get().fetchEnrolledPhotos()
    set({ initialized: true })
  },

  async updateAssignedStaff(enrollmentId, staffUids) {
    try {
      await updateDoc(doc(db, 'org_enrollments', enrollmentId), { assignedStaffUids: staffUids })
      set(s => ({
        enrollments: s.enrollments.map(e =>
          e.id === enrollmentId ? { ...e, assignedStaffUids: staffUids } : e
        ),
      }))
    } catch (err) {
      console.warn('[orgInvites] updateAssignedStaff failed:', err)
      throw err
    }
  },

  assignedEnrollments() {
    const roles = useAuthStore.getState().roles()
    const isAdmin = roles.some(r => r.role === USER_ROLES.ORG_ADMIN)
    if (isAdmin) return get().enrollments
    const uid = useAuthStore.getState().uid()
    if (!uid) return get().enrollments
    return get().enrollments.filter(e => e.assignedStaffUids?.includes(uid))
  },

  familyLabel(uid) {
    return get().familyNames[uid] || `Family ${uid.slice(0, 6).toUpperCase()}`
  },
}))
